import { React, useState, useEffect, useContext } from "react";
import "../../../assets/css/table.css";
import Loading from "react-fullscreen-loading";
import HeaderSection from "../banner/HeaderSection";
import DivLoader from "../../../component/utilities/Spinner";
import DataTable from "react-data-table-component";
import axios from "axios";
import { toast } from "react-toastify";
import * as CONST from "../../../component/config/Constant";
import { AuthContext } from "../../../component/context/AuthContext";
import Moment from "moment";
import Swal from "sweetalert2";
import { FiBox, FiClock, FiCheckCircle, FiXCircle, FiRotateCcw, FiThumbsUp } from "react-icons/fi";
import "../../../assets/css/dashboard.css";

function DepartmentAssetsDashboard() {
  const [authUserData] = useContext(AuthContext);
  const [fullLoading, setFullLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [listLimit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [maxPageLimit, setMaxPageLimit] = useState(10);
  const [minPageLimit, setMinPageLimit] = useState(0);

  // Search
  const [srchAssetName, setSrchAssetName] = useState("");
  const [srchStatus, setSrchStatus] = useState("");
  const [srchUserName, setSrchUserName] = useState("");

  let isToken = "";
  let departmentId = "";
  let departmentName = "";
  if (authUserData && authUserData[0]) {
    isToken = authUserData[0].token || "";
    departmentId = authUserData[0].department_id || "";
    departmentName = authUserData[0].department_name || "";
  }

  useEffect(() => {
    fetchStats();
    fetchRequests();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.post(
        CONST.GET_DEPARTMENT_ASSET_STATS,
        { department_id: departmentId },
        { headers: { Authorization: `Bearer ${isToken}` } }
      );
      if (response.data.status === "success") {
        setStats(response.data.data);
      }
    } catch (error) {
      // silently handle
    }
  };

  const fetchRequests = async (page) => {
    setLoading(true);
    try {
      const response = await axios.post(
        CONST.GET_ADMIN_ASSETS_LIST,
        {
          page_number: page || activePage,
          limit: listLimit,
          department_id: departmentId,
          search_input: {
            srchAssetName: srchAssetName,
            srchAssetStatus: srchStatus,
            srchUserName: srchUserName,
          },
          sort: { order_by: "id", order: "desc" },
        },
        { headers: { Authorization: `Bearer ${isToken}` } }
      );
      if (response.data.status === "success") {
        setRequests(response.data.data);
        setTotalRows(response.data.count);
        setTotalPages(response.data.totalPages);
      } else {
        setRequests([]);
        setTotalRows(0);
      }
    } catch (error) {
      // silently handle
    }
    setLoading(false);
  };

  const handleSearch = () => {
    setActivePage(1);
    fetchRequests(1);
  };

  const handleClear = () => {
    setSrchAssetName("");
    setSrchStatus("");
    setSrchUserName("");
    setActivePage(1);
    setTimeout(() => fetchRequests(1), 100);
  };

  const handlePageChange = (page) => {
    setActivePage(page);
    fetchRequests(page);
  };

  const handleHeadAction = async (id, action) => {
    const actionText = action === "approve" ? "approve" : "reject";
    const result = await Swal.fire({
      title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} this request?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}`,
      confirmButtonColor: action === "approve" ? "#059669" : "#dc2626",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          CONST.HEAD_APPROVE_ASSET,
          { id, action },
          { headers: { Authorization: `Bearer ${isToken}` } }
        );
        if (response.data.status === "success") {
          toast.success(response.data.message);
          fetchRequests();
          fetchStats();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error("Something went wrong.");
      }
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      "0": { label: "New Request", className: "bg-warning text-dark" },
      "1": { label: "In Process", className: "", style: { backgroundColor: "#fff3cd", color: "#856404" } },
      "2": { label: "Allocated", className: "", style: { backgroundColor: "#d4edda", color: "#155724" } },
      "3": { label: "Rejected", className: "bg-danger" },
      "4": { label: "Returned", className: "", style: { backgroundColor: "#e2e8f0", color: "#475569" } },
      "5": { label: "Head Approved", className: "", style: { backgroundColor: "#dbeafe", color: "#1e40af" } },
    };
    const s = map[status] || { label: "Unknown", className: "bg-secondary" };
    return <span className={`badge rounded-pill ${s.style ? "" : s.className}`} style={s.style || {}}>{s.label}</span>;
  };

  const columns = [
    {
      name: "#",
      width: "50px",
      cell: (row, index) => index + 1,
    },
    {
      name: "Employee",
      selector: (row) => row.empData?.first_name,
      cell: (row) => (
        <div>
          <div className="fw-bold small">{row.empData?.first_name} {row.empData?.last_name}</div>
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>{row.empData?.designation?.name}</div>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Asset Type",
      selector: (row) => row.e_asset_type?.name,
      cell: (row) => row.e_asset_type?.name || "-",
      sortable: true,
    },
    {
      name: "Brand",
      cell: (row) => row.assetBrandDetails?.brand_name || "-",
    },
    {
      name: "Qty",
      width: "60px",
      cell: (row) => row.quantity || 1,
    },
    {
      name: "Requested",
      cell: (row) => row.request_create_date ? Moment(row.request_create_date).format("DD-MM-YYYY") : Moment(row.created_at).format("DD-MM-YYYY"),
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => getStatusBadge(row.request_status),
    },
    {
      name: "Action",
      width: "160px",
      cell: (row) => {
        if (row.request_status === "0") {
          return (
            <div className="d-flex gap-1">
              <button
                className="btn btn-outline-success btn-sm"
                onClick={() => handleHeadAction(row.id, "approve")}
                style={{ fontSize: "0.75rem", padding: "2px 8px" }}
              >
                <FiCheckCircle size={13} className="me-1" />Approve
              </button>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleHeadAction(row.id, "reject")}
                style={{ fontSize: "0.75rem", padding: "2px 8px" }}
              >
                <FiXCircle size={13} className="me-1" />Reject
              </button>
            </div>
          );
        }
        return <span className="text-muted small">—</span>;
      },
    },
  ];

  const pageNumberLimit = { perPage: listLimit };

  function toPages(pages) {
    const r = [];
    for (let i = 1; i <= pages; i++) r.push(i);
    return r;
  }

  function getNumberOfPages(rowCount, rowsPerPage) {
    return Math.ceil(rowCount / rowsPerPage);
  }

  const BootyPagination = ({
    rowsPerPage,
    rowCount,
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
  }) => {
    const handleBackButtonClick = async () => {
      if ((currentPage - 1) % pageNumberLimit.perPage === 0) {
        setMaxPageLimit(maxPageLimit - pageNumberLimit.perPage);
        setMinPageLimit(minPageLimit - pageNumberLimit.perPage);
      }
      onChangePage(currentPage - 1);
    };

    const handleNextButtonClick = async () => {
      if (currentPage + 1 > maxPageLimit) {
        setMaxPageLimit(maxPageLimit + pageNumberLimit.perPage);
        setMinPageLimit(minPageLimit + pageNumberLimit.perPage);
      }
      onChangePage(currentPage + 1);
    };

    const handlePageNumber = (e) => {
      onChangePage(Number(e.target.value));
    };

    let paginationText = "Page:  " + currentPage + ", Showing ";
    paginationText =
      paginationText +
      ((currentPage - 1) * listLimit + 1) +
      " to " +
      listLimit * currentPage;
    paginationText = paginationText + " of " + rowCount + " entries;";

    const pages = getNumberOfPages(rowCount, rowsPerPage);
    const pageItems = toPages(pages);
    const nextDisabled = currentPage === pageItems.length;
    const previosDisabled = currentPage === 1;

    const pageNumbers = pageItems.map((page) => {
      if (page <= maxPageLimit && page > minPageLimit) {
        return (
          <li
            key={page}
            id={page}
            className={`page-item ${currentPage === page ? "active" : ""}`}
          >
            <button
              className={`page-link ${currentPage === page ? "active" : ""}`}
              onClick={handlePageNumber}
              value={page}
            >
              {page}
            </button>
          </li>
        );
      } else {
        return null;
      }
    });

    let pageIncrementEllipses = null;
    if (pageItems.length > maxPageLimit) {
      pageIncrementEllipses = (
        <li className="page-item" onClick={handleNextButtonClick}>
          &hellip;
        </li>
      );
    }
    let pageDecremenEllipses = null;
    if (minPageLimit >= 1) {
      pageDecremenEllipses = (
        <li className="page-item" onClick={handleBackButtonClick}>
          &hellip;
        </li>
      );
    }

    return (
      <div className="card-footer d-flex align-items-center">
        <p className="m-0 text-muted">{paginationText}</p>
        <ul className="pagination m-0 ms-auto">
          <li className="page-item">
            <button
              className="page-link"
              onClick={handleBackButtonClick}
              disabled={previosDisabled}
              aria-disabled={previosDisabled}
              aria-label="previous page"
              pagerangedisplayed={10}
            >
              &laquo;
            </button>
          </li>
          {pageDecremenEllipses}
          {pageNumbers}
          {pageIncrementEllipses}
          <li className="page-item">
            <button
              className="page-link"
              onClick={handleNextButtonClick}
              disabled={nextDisabled}
              aria-disabled={nextDisabled}
              aria-label="next page"
            >
              &raquo;
            </button>
          </li>
        </ul>
      </div>
    );
  };

  const StatCard = ({ icon, label, value, bgColor, color }) => (
    <div className="col-6 col-md-3">
      <div className="db-stat-card">
        <div className="db-stat-icon" style={{ background: bgColor, color: color }}>
          {icon}
        </div>
        <div className="db-stat-value">{value}</div>
        <div className="db-stat-label">{label}</div>
      </div>
    </div>
  );

  return (
    <>
      <Loading loading={fullLoading} background="rgba(0, 0, 0, 0.2)" loaderColor="#1167e8" />
      <HeaderSection title={`Department Assets — ${departmentName}`} />

      <div className="container-fluid px-4 py-3">
        {/* Stats Cards */}
        {stats && (
          <div className="row db-stats-row">
            <StatCard icon={<FiClock size={18} />} label="Pending Requests" value={stats.newRequests} bgColor="#fef3c7" color="#b45309" />
            <StatCard icon={<FiThumbsUp size={18} />} label="Head Approved" value={stats.headApproved} bgColor="#dbeafe" color="#1167e8" />
            <StatCard icon={<FiBox size={18} />} label="Allocated" value={stats.allocated} bgColor="#d1fae5" color="#059669" />
            <StatCard icon={<FiRotateCcw size={18} />} label="Returned" value={stats.returned} bgColor="#f1f5f9" color="#6b7280" />
          </div>
        )}

        {/* Filters */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Listing</h3>
          </div>
          <div className="card-body border-bottom py-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1">Employee Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={srchUserName}
                  onChange={(e) => setSrchUserName(e.target.value)}
                  placeholder="Search employee..."
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1">Asset Type</label>
                <input
                  type="text"
                  className="form-control"
                  value={srchAssetName}
                  onChange={(e) => setSrchAssetName(e.target.value)}
                  placeholder="Search asset..."
                />
              </div>
              <div className="col-md-2">
                <label className="form-label small fw-bold mb-1">Status</label>
                <select
                  className="form-select form-control"
                  value={srchStatus}
                  onChange={(e) => setSrchStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="0">New Request</option>
                  <option value="5">Head Approved</option>
                  <option value="1">In Process</option>
                  <option value="2">Allocated</option>
                  <option value="3">Rejected</option>
                  <option value="4">Returned</option>
                </select>
              </div>
              <div className="col-md-2 d-flex gap-2">
                <button className="btn btn-blue d-none d-sm-inline-block mr-3" onClick={handleSearch}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><circle cx="10" cy="10" r="7"></circle><line x1="21" y1="21" x2="15" y2="15"></line></svg>
                  Search
                </button>
                <button className="btn btn-outline-primary mr-3" onClick={handleClear}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Requests Table */}
        <div className="card">
          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={requests}
              progressPending={loading}
              progressComponent={<DivLoader />}
              pagination="true"
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={listLimit}
              paginationDefaultPage={activePage}
              onChangePage={handlePageChange}
              paginationComponent={BootyPagination}
              noDataComponent={<div className="p-4 text-muted">No asset requests found for your department.</div>}
            />
          </div>
          <div className="card-footer d-flex align-items-center">
            <p className="m-0 text-muted">Showing <span>{totalRows}</span> entries</p>
          </div>
        </div>
      </div>
    </>
  );
}

export default DepartmentAssetsDashboard;
